import React, { useState, useRef, useEffect } from 'react'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const fmt = n => '£' + Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })
const getTime = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

const QUESTIONS = [
  { key: 'businessType', text: '1️⃣ What type of business is this client?', quick: ['Supported living / care', 'Property management', 'Catering / restaurant', 'Construction', 'Retail', 'Freelancer'] },
  { key: 'purchasePayees', text: '2️⃣ Are any payees suppliers or purchases for the business? List names or say "none"', quick: ['None — all standard expenses'] },
  { key: 'staffNames', text: '3️⃣ Any staff names I should know? Or say "auto-detect"', quick: ['Auto-detect'] },
]

const PL = [
  ['TURNOVER', [['Sales / Income', 'Sales/Income', true]]],
  ['COST OF SALES', [['Purchases', 'Purchases'], ['Other direct costs', 'Other Direct Costs']]],
  ['STAFF', [["Directors' remuneration", "Directors' renumeration"], ["Directors' pension", "Directors' pension"], ['Salaries and wages', 'Salaries and wages'], ["Employer's NIC", "Employer's NIC"]]],
  ['PROFESSIONAL', [['Accountancy and audit', 'Accountancy and audit'], ['Consultancy', 'Consultancy'], ['Legal and professional', 'Legal and professional charges']]],
  ['PROPERTY', [['Rent and rates', 'Rent and rates.'], ['Light, heat and power', 'Light, heat and power:'], ['Repairs and maintenance', 'Repairs, renewals and maintenance.']]],
  ['ADMIN & OTHER', [['Administration and office', 'Administration and office expenses'], ['Vehicle expenses', 'Vehicle expenses '], ['Travel and subsistence', 'Travel and subsistence'], ['Insurance', 'Insurance'], ['Bank charges', 'Bank, credit card and other financial charges'], ['Interest paid', 'Interest paid'], ['Entertaining', 'Entertaining'], ['Sundry expenses', 'Sundry expenses (small miscellaneous expenses only)']]],
]

const WELCOME_MSG = { role: 'ai', text: 'Good morning. Upload a bank statement CSV or drag and drop a file below — I\'ll ask a few quick questions about the client, then categorise every transaction into the correct P&L rows.', time: getTime(), upload: true }

// ── Robust CSV parser — handles quoted fields with commas inside ──────────────
function parseCSVLine(line) {
  const cols = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      cols.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  cols.push(current.trim())
  return cols
}

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim())
  const start = lines[0].toLowerCase().includes('date') ? 1 : 0
  const result = []

  for (let i = start; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i])
    if (cols.length < 3) continue

    const date = cols[0].replace(/"/g, '').trim()
    if (!date) continue

    // Amount is always the last column
    const rawAmt = cols[cols.length - 1].replace(/"/g, '').trim()
    const isNegative = rawAmt.startsWith('-')
    const amt = parseFloat(rawAmt.replace(/[^0-9.\-]/g, ''))
    if (isNaN(amt)) continue

    // Description is everything in between — clean it up
    const descParts = cols.slice(1, cols.length - 1)
    let desc = descParts.join(' ').replace(/"/g, '').replace(/\s+/g, ' ').trim()
    if (!desc) desc = cols[1] ? cols[1].replace(/"/g, '').trim() : ''
    if (!desc) continue

    // Sanitise description — remove control characters that break JSON
    desc = desc.replace(/[\x00-\x1F\x7F]/g, ' ').trim()

    result.push({
      date,
      description: desc,
      amount: Math.abs(amt),
      direction: isNegative ? 'out' : 'in'
    })
  }
  return result
}

export default function BankStatementPL() {
  const [msgs, setMsgs] = useState([WELCOME_MSG])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [stage, setStage] = useState('welcome')
  const [txns, setTxns] = useState([])
  const [qIdx, setQIdx] = useState(0)
  const [answers, setAnswers] = useState({})
  const [plTotals, setPlTotals] = useState(null)
  const [categorisedTxns, setCategorisedTxns] = useState([])
  const [flags, setFlags] = useState([])
  const bottomRef = useRef(null)
  const fileRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [msgs])

  function addMsg(role, text, opts) {
    setMsgs(prev => [...prev, { role, text, time: getTime(), ...opts }])
  }

  function loadStatement(text, filename) {
    const parsed = parseCSV(text)
    if (parsed.length < 2) {
      addMsg('ai', "⚠️ Couldn't parse that file. Make sure it's a CSV with date, description and amount columns.")
      return
    }
    setTxns(parsed)
    setStage('questioning')
    setQIdx(0)
    setAnswers({})

    const totalIn  = parsed.filter(t => t.direction === 'in').reduce((s, t) => s + t.amount, 0)
    const totalOut = parsed.filter(t => t.direction === 'out').reduce((s, t) => s + t.amount, 0)

    addMsg('ai', `✅ ${parsed.length} transactions loaded from ${filename}.\n\n📥 Income: ${fmt(totalIn)}\n📤 Expenses: ${fmt(totalOut)}\n\nBefore I categorise everything, I need to understand this client.`)
    setTimeout(() => addMsg('ai', QUESTIONS[0].text, { quick: QUESTIONS[0].quick }), 600)
  }

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => loadStatement(ev.target.result, file.name)
    reader.readAsText(file)
    e.target.value = ''
  }

  function handleDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => loadStatement(ev.target.result, file.name)
    reader.readAsText(file)
  }

  async function runAnalysis(finalAnswers) {
    setLoading(true)
    const OPENAI_KEY = process.env.REACT_APP_OPENAI_API_KEY || ''
    if (!OPENAI_KEY) {
      addMsg('ai', '⚠️ OpenAI API key not configured. Please add REACT_APP_OPENAI_API_KEY to Vercel environment variables.')
      setLoading(false)
      return
    }

    const total = txns.length
    const BATCH = 60
    const batches = []
    for (let i = 0; i < txns.length; i += BATCH) {
      batches.push(txns.slice(i, i + BATCH))
    }
    const numBatches = batches.length
    addMsg('ai', `🚀 Processing ${total} transactions directly via GPT-4o (${numBatches} batches)...`)

    const CATEGORIES = `Sales/Income | Purchases | Other Direct Costs | Directors' pension | Directors' renumeration | Salaries and wages | Subcontractors' payments (construction industry only) | Accountancy and audit | Consultancy | Legal and professional charges | Employer's NIC | Light, heat and power: | Rent and rates. | Repairs, renewals and maintenance. | Advertising and promotions | Bad debts | Bank, credit card and other financial charges | Depreciation | Donations | Entertaining | Insurance | Interest paid | Travel and subsistence | Vehicle expenses  | Administration and office expenses | Sundry expenses (small miscellaneous expenses only)`

    const systemPrompt = `You are a UK accounting assistant for Sunshoos Accountants, East London.
This client runs a ${finalAnswers.businessType || 'business'} business.${finalAnswers.purchasePayees && finalAnswers.purchasePayees.toLowerCase() !== 'none' ? '\n- These payees are PURCHASES: ' + finalAnswers.purchasePayees : ''}${finalAnswers.staffNames && finalAnswers.staffNames.toLowerCase() !== 'auto-detect' ? '\n- These are staff: ' + finalAnswers.staffNames : ''}

Categorise EVERY transaction into exactly one of these P&L categories (use verbatim):
${CATEGORIES}

Rules:
- Credits (direction=in) = Sales/Income
- HMRC PAYE/NIC payments = Employer's NIC
- Pension (NEST etc) = Directors' pension
- Director named payments = Directors' renumeration
- Staff wage payments = Salaries and wages
- Property rent = Rent and rates.
- Gas/electric/utilities = Light, heat and power:
- Petrol/fuel/DVLA = Vehicle expenses
- Flights/hotels/rail/TFL = Travel and subsistence
- Accountant fees = Accountancy and audit
- IT/web/software = Consultancy
- Bank charges/card fees = Bank, credit card and other financial charges
- Loan repayments = Interest paid
- Broadband/phone/subscriptions = Administration and office expenses
- Food/household items = Purchases
- Insurance premiums = Insurance

Return ONLY valid JSON:
{"transactions":[{"date":"str","description":"str","amount":0.0,"direction":"in or out","pl_category":"exact category","confidence":"high or medium or low","note":"brief reason or null"}]}`

    try {
      let allTxns = []
      const combinedTotals = {}
      const allFlags = []

      for (let b = 0; b < batches.length; b++) {
        const batch = batches[b]
        const txnText = batch.map(t =>
          `${t.date}  ${t.description.substring(0, 40).padEnd(40)}  ${t.direction === 'in' ? 'CR' : 'DR'}  £${t.amount.toFixed(2)}`
        ).join('\n')

        const res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${OPENAI_KEY}`
          },
          body: JSON.stringify({
            model: 'gpt-4o',
            temperature: 0.1,
            response_format: { type: 'json_object' },
            max_tokens: 4000,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: `Categorise these ${batch.length} transactions:\n\n${txnText}` }
            ]
          })
        })

        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(`OpenAI error on batch ${b + 1}: ${err.error?.message || res.statusText}`)
        }

        const data = await res.json()
        const parsed = JSON.parse(data.choices[0].message.content)
        const batchTxns = parsed.transactions || []

        // Merge
        allTxns = allTxns.concat(batchTxns)
        batchTxns.forEach(t => {
          const cat = t.pl_category || 'Sundry expenses (small miscellaneous expenses only)'
          const amt = t.amount || 0
          const val = t.direction === 'in' ? amt : -amt
          combinedTotals[cat] = (combinedTotals[cat] || 0) + val
        })

        // Low confidence flags
        const low = batchTxns.filter(t => t.confidence === 'low')
        if (low.length > 0) allFlags.push(`${low.length} transactions in batch ${b+1} have low confidence — review recommended`)

        // Update P&L live
        setPlTotals({ ...combinedTotals })
        setCategorisedTxns([...allTxns])

        // Progress update every 5 batches
        if ((b + 1) % 5 === 0 || b === numBatches - 1) {
          const done = b + 1
          const pct = Math.round((done / numBatches) * 100)
          addMsg('ai', `⏳ ${pct}% — ${done * BATCH > total ? total : done * BATCH} of ${total} transactions processed...`)
        }
      }

      // Sundry flag
      const sundry = Math.abs(combinedTotals['Sundry expenses (small miscellaneous expenses only)'] || 0)
      if (sundry > 500) allFlags.push(`Sundry expenses total £${sundry.toLocaleString('en-GB', {minimumFractionDigits:2})} — review with client`)

      const uniqueFlags = [...new Set(allFlags)]
      setFlags(uniqueFlags)
      setStage('done')

      const income   = combinedTotals['Sales/Income'] || 0
      const expenses = Object.entries(combinedTotals)
        .filter(([k]) => k !== 'Sales/Income')
        .reduce((s, [, v]) => s + Math.abs(v), 0)
      const net = income - expenses

      addMsg('ai',
        `✅ Done — ${total} transactions categorised.\n\n💰 Income: ${fmt(income)}\n💸 Expenses: ${fmt(expenses)}\n📊 Net ${net >= 0 ? 'profit' : 'loss'}: ${fmt(Math.abs(net))}\n\nReview the P&L on the right. Tell me if anything needs correcting — e.g. "Move Ryanair to Travel" or "Sarumi should be Directors remuneration".`,
        { quick: ['Looks correct ✓', 'Something needs correcting'] }
      )
    } catch (err) {
      addMsg('ai', `⚠️ Error: ${err.message}`)
      setStage('welcome')
    }
    setLoading(false)
  }

  async function handleMessage(text) {
    if (stage === 'questioning') {
      const q = QUESTIONS[qIdx]
      const newAnswers = { ...answers, [q.key]: text }
      setAnswers(newAnswers)
      const nextIdx = qIdx + 1
      if (nextIdx < QUESTIONS.length) {
        setQIdx(nextIdx)
        setTimeout(() => addMsg('ai', QUESTIONS[nextIdx].text, { quick: QUESTIONS[nextIdx].quick }), 400)
      } else {
        await runAnalysis(newAnswers)
      }
    } else if (stage === 'done') {
      // Handle correction requests
      if (text.toLowerCase().includes('correct') && !text.toLowerCase().includes('incorrect')) {
        addMsg('ai', '✅ Great! The P&L is locked in. You can now download the Excel file using the button below.')
      } else {
        addMsg('ai', `Got it — I'll flag "${text}" for manual review. You can also download the Excel and adjust any categories directly in the spreadsheet.`)
      }
    }
  }

  function send() {
    const txt = input.trim()
    if (!txt || loading) return
    addMsg('user', txt)
    setInput('')
    handleMessage(txt)
  }

  // Compute net for display
  const income   = plTotals ? (plTotals['Sales/Income'] || 0) : 0
  const expenses = plTotals ? Object.entries(plTotals).filter(([k]) => k !== 'Sales/Income').reduce((s, [, v]) => s + Math.abs(v), 0) : 0
  const net = income - expenses

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', fontFamily: "'DM Sans', 'Calibri', sans-serif", background: '#F8F9FB' }}>

      {/* ── Chat panel ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, borderRight: '0.5px solid #e0e3e9' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, flexDirection: m.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start' }}>
              {m.role === 'ai' && (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#C9973A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#0B1929', flexShrink: 0 }}>S</div>
              )}
              <div style={{ maxWidth: '75%' }}>
                <div style={{
                  padding: '10px 14px',
                  background: m.role === 'user' ? '#0B1929' : 'white',
                  color: m.role === 'user' ? '#C9973A' : '#1a1a2e',
                  border: m.role === 'ai' ? '0.5px solid #e0e3e9' : 'none',
                  borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}>
                  {m.text}
                </div>

                {/* Upload area */}
                {m.upload && stage === 'welcome' && (
                  <div
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => fileRef.current?.click()}
                    style={{ marginTop: 10, padding: '20px 16px', background: 'white', border: '2px dashed rgba(201,151,58,0.35)', borderRadius: 10, textAlign: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 7 }}>📂</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e', marginBottom: 4 }}>Drop bank statement here or click to upload</div>
                    <div style={{ fontSize: 11, color: '#999' }}>Starling, Barclays, HSBC, Monzo — any CSV format</div>
                    <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFile} />
                  </div>
                )}

                {/* Quick replies */}
                {m.quick && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {m.quick.map((q, qi) => (
                      <button key={qi}
                        onClick={() => { addMsg('user', q); handleMessage(q) }}
                        style={{ fontSize: 11, padding: '5px 11px', borderRadius: 20, border: '1px solid rgba(201,151,58,0.3)', background: 'rgba(201,151,58,0.07)', color: '#7A4A10', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 10, color: '#bbb', marginTop: 4, textAlign: m.role === 'user' ? 'right' : 'left' }}>{m.time}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#C9973A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#0B1929' }}>S</div>
              <div style={{ padding: '12px 16px', background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, fontSize: 13, color: '#888' }}>
                Thinking...
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '12px 14px', borderTop: '0.5px solid #e0e3e9', background: 'white', display: 'flex', gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send() }}
            placeholder={stage === 'questioning' ? 'Type your answer...' : stage === 'done' ? 'Request a correction or ask a question...' : 'Type or upload above...'}
            style={{ flex: 1, border: '0.5px solid #ccc', borderRadius: 9, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            style={{ width: 38, height: 38, background: input.trim() && !loading ? '#0B1929' : '#eee', border: 'none', borderRadius: 9, cursor: input.trim() && !loading ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M14 8L2 2l2 6-2 6 12-6z" fill={input.trim() && !loading ? '#C9973A' : '#aaa'} />
            </svg>
          </button>
        </div>
      </div>

      {/* ── P&L panel ── */}
      <div style={{ width: 300, display: 'flex', flexDirection: 'column', background: 'white', flexShrink: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', borderBottom: '0.5px solid #e0e3e9', background: '#f7f8fa', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>P&L Account</div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 2, fontFamily: 'monospace' }}>
            {plTotals ? `${txns.length} transactions · ${answers.businessType || 'Business'}` : 'Upload a statement to begin'}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {!plTotals ? (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: '#aaa' }}>
              <div style={{ fontSize: 32, marginBottom: 10, opacity: .25 }}>📊</div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>Upload a bank statement and answer the questions — the P&L will populate here automatically</div>
            </div>
          ) : (
            <>
              {PL.map(([sec, rows]) => {
                const visible = rows.filter(([, key]) => plTotals[key])
                if (!visible.length) return null
                return (
                  <div key={sec} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#bbb', padding: '8px 6px 3px' }}>{sec}</div>
                    {visible.map(([label, key, isIncome]) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 6px', borderRadius: 5, marginBottom: 1, background: isIncome ? 'rgba(59,109,17,0.05)' : 'transparent', borderLeft: isIncome ? '2px solid #3B6D11' : 'none' }}>
                        <span style={{ fontSize: 11, color: '#666' }}>{label}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, fontFamily: 'monospace', color: isIncome ? '#3B6D11' : '#A32D2D' }}>{fmt(Math.abs(plTotals[key]))}</span>
                      </div>
                    ))}
                  </div>
                )
              })}

              <div style={{ marginTop: 10, padding: '10px 8px', borderRadius: 8, background: net >= 0 ? 'rgba(59,109,17,0.07)' : 'rgba(163,45,45,0.07)', borderLeft: `3px solid ${net >= 0 ? '#3B6D11' : '#A32D2D'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>Net {net >= 0 ? 'Profit' : 'Loss'}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: net >= 0 ? '#3B6D11' : '#A32D2D' }}>{fmt(Math.abs(net))}</span>
                </div>
              </div>

              {flags.length > 0 && (
                <div style={{ marginTop: 10, background: '#FFF8E6', borderRadius: 8, padding: '9px 10px', fontSize: 11, color: '#854F0B', lineHeight: 1.7 }}>
                  <strong>⚠ Review with client:</strong><br />
                  {flags.map((f, i) => <div key={i}>· {f}</div>)}
                </div>
              )}

              <div style={{ marginTop: 8, background: 'rgba(59,109,17,0.06)', borderRadius: 8, padding: '8px 10px', fontSize: 10, color: '#3B6D11' }}>
                🧠 Client profile saved — same payees recognised next time
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '10px 12px', borderTop: '0.5px solid #e0e3e9', background: '#f7f8fa', flexShrink: 0 }}>
          <button
            disabled={!plTotals}
            style={{ width: '100%', background: plTotals ? '#0B1929' : '#e4e8ee', color: plTotals ? '#C9973A' : '#aaa', border: 'none', borderRadius: 8, padding: 9, fontSize: 12, fontWeight: 600, cursor: plTotals ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}
            onClick={async () => {
              try {
                const res = await fetch(`${API}/api/bank-statement/export-pl`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    pl_totals: plTotals,
                    transactions: categorisedTxns,
                    business_type: answers.businessType || 'Business',
                    client_name: answers.clientName || 'Client',
                    company_number: answers.companyNumber || '',
                    period: answers.period || 'Full Year',
                    transaction_count: txns.length
                  })
                })
                if (!res.ok) throw new Error('Export failed')
                const blob = await res.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `PL_${(answers.businessType || 'Client').replace(/ /g,'_')}.xlsx`
                document.body.appendChild(a)
                a.click()
                a.remove()
                window.URL.revokeObjectURL(url)
              } catch (err) {
                alert('Download error: ' + err.message)
              }
            }}>
            ⬇ Download P&L Excel
          </button>
        </div>
      </div>
    </div>
  )
}
